import React from 'react';
import PropTypes from 'prop-types';
import parse from 'html-react-parser';
import styled from '@emotion/styled';

import {
  getFieldSummaryLabel,
  renderFieldValue,
} from 'features/consults/utils/formFields';
import useSummaryTabState from 'features/consults/hooks/useSummaryTabState';
import demographicsFields from 'features/consults/shared/tabs/Demographics/formFields';
import { Box } from 'styles/layout';
import {
  fontStyle,
  PageTitle,
  SectionHeader,
  FieldRow,
  LineBreak,
  SectionContainer,
} from './SummaryNote.style';
import AddendumList from './AddendumList';

const SavedSummary = styled(Box)`
  & div [style='white-space: pre;'] {
    height: 0 !important;
  }
`;

const renderBlankSpace = <LineBreak>{'\r'}</LineBreak>;

const SummaryNote = React.forwardRef(
  ({ addenda = [], copyRef, isClosed }, ref) => {
    const { consult, title, summarySections, facilityTimezone, facilityName } =
      useSummaryTabState();

    const renderTabData = React.useMemo(
      () =>
        summarySections.map(({ sectionTitle, summaryFields }) => (
          <SectionContainer key={sectionTitle}>
            <SectionHeader>{`# ${sectionTitle}`}</SectionHeader>
            {summaryFields.map((field) => (
              <FieldRow key={field.name}>
                <b>{getFieldSummaryLabel(field, facilityTimezone)}</b>
                {renderFieldValue({
                  field,
                  facilityTimezone,
                  facilityName,
                  consult,
                })}
                {renderBlankSpace}
              </FieldRow>
            ))}
          </SectionContainer>
        )),
      [summarySections, facilityTimezone, facilityName, consult],
    );

    const renderDemographicsFooter = React.useMemo(
      () => (
        <SectionContainer marginBottom="3px">
          <SectionHeader># Demographics</SectionHeader>
          {summarySections[0]?.summaryFields
            .filter((element) =>
              [
                demographicsFields.firstName.name,
                demographicsFields.lastName.name,
                demographicsFields.facility.name,
              ].includes(element.name),
            )
            .map((field) => (
              <FieldRow key={field.name} marginBottom="3px">
                <b>{getFieldSummaryLabel(field, facilityTimezone)}</b>
                {renderFieldValue({
                  field,
                  facilityTimezone,
                  facilityName,
                  consult,
                })}
                {renderBlankSpace}
              </FieldRow>
            ))}
        </SectionContainer>
      ),
      [summarySections, facilityTimezone, facilityName, consult],
    );

    const renderAddenda = React.useMemo(
      () =>
        addenda?.length ? (
          <AddendumList addenda={addenda} facilityTimezone={facilityTimezone} />
        ) : null,
      [addenda, facilityTimezone],
    );

    const renderSavedHTML = React.useMemo(
      () => consult.summaryFormatted && parse(consult.summaryFormatted),
      [consult.summaryFormatted],
    );

    return (
      <SavedSummary style={fontStyle} ref={copyRef}>
        {isClosed && renderAddenda}
        <Box style={fontStyle} ref={ref} pt={2}>
          {(isClosed && renderSavedHTML) || (
            <>
              <PageTitle>{`Blue Sky ${title} Note`}</PageTitle>
              {renderBlankSpace}
              {renderTabData}
              {renderDemographicsFooter}
              {renderBlankSpace}
            </>
          )}
        </Box>
      </SavedSummary>
    );
  },
);

SummaryNote.propTypes = {
  addenda: PropTypes.arrayOf(PropTypes.shape({})),
  copyRef: PropTypes.shape({}),
  isClosed: PropTypes.bool,
};

export default SummaryNote;
